// OMCL taxonomy used by the Automation Maturity Intake.
//
// Extracted verbatim from the authoritative source at
// `OMC - Time Estimation & Automation/build_omcl_mapped_tab.py`.
// Each sub-discipline maps to a parent V2 discipline id used by the engine
// (see data/models/global-model.json -> disciplines[].id).

export interface OmclPhase {
  id: string;
  label: string;
}

export interface OmclSubPhase {
  id: string;
  label: string;
  phaseId: string;
}

export interface SubDiscipline {
  /** Stable key used for tagging on intake entries. */
  key: string;
  discipline: string;
  channel: string;
  /** V2 discipline id this rolls up to in the engine model. */
  parentDisciplineId: string;
}

/**
 * Time that is not attributable to campaign work — internal meetings, people
 * management, training, new business and so on. It holds non-campaign *working*
 * time only: no PTO, holiday or sick time, since FTE_CAPACITY already excludes
 * those.
 *
 * Every level column of `model.timeConcentration` sums to 100% across all six
 * phases, so this phase is what makes the campaign shares add up to less than
 * one. Automations that target campaign sub-phases therefore release a share of
 * total time rather than a share of campaign time.
 */
export const NON_CAMPAIGN_PHASE_ID = 'p6';

export const OMCL_PHASES: readonly OmclPhase[] = [
  { id: 'p1', label: 'P1 — Business Planning' },
  { id: 'p2', label: 'P2 — Strategic & Tactical Planning' },
  { id: 'p3', label: 'P3 — Creative & Content Development' },
  { id: 'p4', label: 'P4 — Execution & Activation' },
  { id: 'p5', label: 'P5 — Measure & Optimize' },
  { id: NON_CAMPAIGN_PHASE_ID, label: 'P6 — Other / Non-Campaign' },
] as const;

const sub = (id: string, label: string, phaseId: string): OmclSubPhase => ({
  id,
  label,
  phaseId,
});

export const OMCL_SUB_PHASES: readonly OmclSubPhase[] = [
  sub('annual_strategic_planning', 'Annual Strategic Planning', 'p1'),
  sub('cross_functional_prioritization', 'Cross-Functional Prioritization', 'p1'),
  sub('in_year_planning', 'In-Year Planning', 'p1'),
  sub('integrated_campaign_brief', 'Integrated Campaign Brief Development', 'p2'),
  sub('media_briefing_strategy', 'Media Briefing & Strategy', 'p2'),
  sub('media_planning', 'Media Planning', 'p2'),
  sub('tactical_planning', 'Tactical Planning', 'p2'),
  sub('creative_development_production', 'Creative Development & Production', 'p3'),
  sub('placement_platform_build', 'Placement & Platform Build', 'p4'),
  sub('tagging_campaign_qa', 'Tagging & Campaign QA', 'p4'),
  sub('launch_activate', 'Launch/Activate', 'p4'),
  sub('real_time_optimization', 'Real-Time Optimization', 'p5'),
  sub('weekly_optimization', 'Weekly Optimization', 'p5'),
  sub('retrospective_closeout', 'Retrospective & Close-Out', 'p5'),
  sub('other_internal_admin', 'Internal Administration & Timekeeping', NON_CAMPAIGN_PHASE_ID),
  sub('other_internal_meetings', 'Internal Meetings & Communications', NON_CAMPAIGN_PHASE_ID),
  sub('other_people_management', 'People Management & Development', NON_CAMPAIGN_PHASE_ID),
  sub('other_training_certification', 'Training & Certification', NON_CAMPAIGN_PHASE_ID),
  sub('other_new_business', 'New Business & Pitch Support', NON_CAMPAIGN_PHASE_ID),
  sub('other_commercial_scope', 'Client Commercial & Scope Management', NON_CAMPAIGN_PHASE_ID),
  sub(
    'other_capability_development',
    'Capability, Product & Process Development',
    NON_CAMPAIGN_PHASE_ID,
  ),
  sub(
    'other_partner_management',
    'Partner & Vendor Relationship Management',
    NON_CAMPAIGN_PHASE_ID,
  ),
  sub('other_thought_leadership', 'Thought Leadership & Industry Engagement', NON_CAMPAIGN_PHASE_ID),
] as const;

// Parent V3 discipline ids (must match data/models/global-model.json).
// Excel-aligned: 7 disciplines, with Investment consolidated across channels.
const BUSINESS_LEADERSHIP = 'business_leadership';
const CLIENT_OPS = 'client_ops';
const STRATEGY = 'strategy';
const PLANNING = 'planning';
const INVESTMENT = 'investment';
const ADOPS = 'adops';
const ANALYTICS = 'analytics';

const sd = (
  discipline: string,
  channel: string,
  parentDisciplineId: string,
): SubDiscipline => ({
  key: `${discipline}_${channel}`,
  discipline,
  channel,
  parentDisciplineId,
});

export const SUB_DISCIPLINES: readonly SubDiscipline[] = [
  sd('Business Leadership', 'All', BUSINESS_LEADERSHIP),
  sd('Client Operations', 'All', CLIENT_OPS),
  sd('Strategy', 'All', STRATEGY),
  sd('Planning', 'All', PLANNING),
  // Investment is one discipline; each channel is a sub-row.
  sd('Investment', 'Digital Direct', INVESTMENT),
  sd('Investment', 'Programmatic', INVESTMENT),
  sd('Investment', 'Social', INVESTMENT),
  sd('Investment', 'Search', INVESTMENT),
  sd('Investment', 'Commerce', INVESTMENT),
  sd('Investment', 'Print', INVESTMENT),
  sd('Investment', 'OOH', INVESTMENT),
  // `channel` must match the model's channel label exactly: mapCellToSubDiscKey
  // matches on the label and silently falls back to the first Investment
  // sub-discipline when nothing matches.
  sd('Investment', 'TV - National', INVESTMENT),
  sd('Investment', 'Audio - National', INVESTMENT),
  sd('Investment', 'TV & Audio - Local', INVESTMENT),
  sd('Investment', 'Other', INVESTMENT),
  sd('AdOps', 'Programmatic + Digital Direct', ADOPS),
  sd('Analytics', 'Data Visualization & Reporting', ANALYTICS),
  sd('Analytics', 'Marketing Science', ANALYTICS),
  sd('Analytics', 'Audience', ANALYTICS),
] as const;

const SUB_PHASE_BY_ID = new Map(OMCL_SUB_PHASES.map((s) => [s.id, s]));
const PHASE_BY_ID = new Map(OMCL_PHASES.map((p) => [p.id, p]));
const SUB_DISCIPLINE_BY_KEY = new Map(SUB_DISCIPLINES.map((s) => [s.key, s]));

export function getSubPhase(id: string): OmclSubPhase | undefined {
  return SUB_PHASE_BY_ID.get(id);
}

/** Whether a sub-phase is non-campaign overhead rather than campaign work. */
export function isNonCampaignSubPhase(subPhaseId: string): boolean {
  return SUB_PHASE_BY_ID.get(subPhaseId)?.phaseId === NON_CAMPAIGN_PHASE_ID;
}

export function getPhase(id: string): OmclPhase | undefined {
  return PHASE_BY_ID.get(id);
}

export function getSubDiscipline(key: string): SubDiscipline | undefined {
  return SUB_DISCIPLINE_BY_KEY.get(key);
}

export function parentDisciplineFor(subDisciplineKey: string): string | undefined {
  return SUB_DISCIPLINE_BY_KEY.get(subDisciplineKey)?.parentDisciplineId;
}

export function subPhasesForPhase(phaseId: string): OmclSubPhase[] {
  return OMCL_SUB_PHASES.filter((s) => s.phaseId === phaseId);
}
